/**
 * 🏠 MonAdresseDomicile — v13.63 Phase 4 (2026-02-28)
 *
 * Édition self-service de l'adresse domicile par l'employé lui-même
 * (own-data = Cat H, aucune clé RBAC nécessaire pour ce cas — le backend
 * autorise déjà l'auto-édition dans `PATCH /api/employees/{id}/adresse-domicile`).
 *
 * L'adresse est utilisée par le calcul kilométrage (§ REGLE_KILOMETRAGE_V13_63)
 * pour reconstituer le trajet domicile → chantier(s) → domicile.
 *
 * Le géocodage est effectué côté backend (Google → fallback Nominatim → cache)
 * lors du PATCH. En cas d'échec, l'adresse texte est quand même sauvegardée
 * — l'employé peut renouveler l'action ou l'admin peut compléter la lat/lng
 * manuellement depuis la fiche employé.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, MapPin, Loader2, CheckCircle2, AlertTriangle, Pencil, X } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function MonAdresseDomicile({ employeeId, token, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ adresse: '', ville: '', code_postal: '' });

  const authHdr = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const fetchData = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/employees/${employeeId}/adresse-domicile`, authHdr);
      setData(r.data);
      setForm({
        adresse: r.data.adresse || '',
        ville: r.data.ville || '',
        code_postal: r.data.code_postal || '',
      });
    } catch {
      // Erreur silencieuse — le composant reste invisible si l'accès est refusé
      setData({ blocked: true });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [employeeId]);

  const handleSubmit = async () => {
    const clean = {
      adresse: form.adresse.trim(),
      ville: form.ville.trim(),
      code_postal: form.code_postal.trim().toUpperCase(),
    };
    if (!clean.adresse) {
      toast.error("L'adresse est obligatoire.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.patch(
        `${API_URL}/api/employees/${employeeId}/adresse-domicile`,
        clean,
        authHdr,
      );
      setData(r.data);
      setEditing(false);
      if (r.data.geocode_disponible) {
        toast.success('Adresse enregistrée et localisée.');
      } else {
        toast.warning(
          'Adresse enregistrée, mais la géolocalisation a échoué. '
          + "Le kilométrage sera indisponible jusqu'à ce que ce soit corrigé.",
        );
      }
      if (onSaved) onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible d'enregistrer l'adresse.";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="text-xs text-slate-400 flex items-center gap-2" data-testid="mon-adresse-domicile-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement de l'adresse…
      </div>
    );
  }

  if (data?.blocked) return null; // 403 silent — rien à afficher

  const hasAddress = !!data?.adresse;
  const hasCoords = !!data?.geocode_disponible;

  // ─── Mode consultation ────────────────────────────────────────────────
  if (!editing) {
    return (
      <Card
        className={`border ${!hasAddress ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200'}`}
        data-testid="mon-adresse-domicile-card"
      >
        <CardContent className="p-3 flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${hasAddress ? 'bg-slate-100' : 'bg-amber-100'}`}
          >
            <Home className={`w-4 h-4 ${hasAddress ? 'text-slate-600' : 'text-amber-700'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Mon adresse de domicile
              </p>
              {hasAddress && hasCoords && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">
                  <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Localisée
                </Badge>
              )}
              {hasAddress && !hasCoords && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]">
                  <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Non localisée
                </Badge>
              )}
            </div>
            {hasAddress ? (
              <p className="text-sm text-slate-800 mt-0.5" data-testid="mon-adresse-domicile-value">
                {data.adresse}
                {data.ville && `, ${data.ville}`}
                {data.code_postal && ` — ${data.code_postal}`}
              </p>
            ) : (
              <p className="text-sm text-amber-800 mt-0.5">
                Aucune adresse enregistrée. Ajoutez-la pour activer le calcul de kilométrage.
              </p>
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              Elle sert uniquement au calcul de vos km payables (aller / retour).
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            data-testid="btn-editer-adresse-domicile"
          >
            <Pencil className="w-3.5 h-3.5 mr-1" />
            {hasAddress ? 'Modifier' : 'Ajouter'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ─── Mode édition ────────────────────────────────────────────────────
  return (
    <Card className="border border-blue-300 bg-blue-50/40" data-testid="mon-adresse-domicile-edit">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" />
            <p className="text-sm font-semibold text-slate-800">Modifier mon adresse de domicile</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setForm({
                adresse: data?.adresse || '',
                ville: data?.ville || '',
                code_postal: data?.code_postal || '',
              });
            }}
            className="text-slate-400 hover:text-slate-600"
            data-testid="btn-annuler-adresse-domicile"
            aria-label="Annuler"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2">
          <div>
            <Label htmlFor="adresse-domicile-adresse" className="text-xs">Adresse (rue et numéro)</Label>
            <Input
              id="adresse-domicile-adresse"
              value={form.adresse}
              onChange={(e) => setForm(f => ({ ...f, adresse: e.target.value }))}
              placeholder="123 rue Principale"
              maxLength={250}
              data-testid="input-adresse-domicile"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="adresse-domicile-ville" className="text-xs">Ville</Label>
              <Input
                id="adresse-domicile-ville"
                value={form.ville}
                onChange={(e) => setForm(f => ({ ...f, ville: e.target.value }))}
                placeholder="Québec"
                maxLength={100}
                data-testid="input-ville-domicile"
              />
            </div>
            <div>
              <Label htmlFor="adresse-domicile-cp" className="text-xs">Code postal</Label>
              <Input
                id="adresse-domicile-cp"
                value={form.code_postal}
                onChange={(e) => setForm(f => ({ ...f, code_postal: e.target.value }))}
                placeholder="G1A 1A1"
                maxLength={10}
                data-testid="input-code-postal-domicile"
              />
            </div>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 flex items-start gap-1">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
          La géolocalisation se fait automatiquement à l'enregistrement — aucune saisie de GPS requise.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(false)}
            disabled={saving}
            data-testid="btn-cancel-adresse-domicile"
          >
            Annuler
          </Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleSubmit}
            disabled={saving || !form.adresse.trim()}
            data-testid="btn-save-adresse-domicile"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
